import { useState, useEffect, useRef, useCallback } from "react";

// ---- color conversions ----
function clamp(v, a, b) { return Math.min(b, Math.max(a, v)); }

function hsvToRgb(h, s, v) {
  s /= 100; v /= 100;
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0, g = 0, b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255) };
}

function rgbToHsv(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60; if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : (d / max) * 100;
  const v = max * 100;
  return { h, s, v };
}

function rgbToHex({ r, g, b }) {
  return "#" + [r, g, b].map((x) => clamp(Math.round(x), 0, 255).toString(16).padStart(2, "0")).join("").toUpperCase();
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60; if (h < 0) h += 360;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function parseColor(str) {
  if (!str) return { r: 59, g: 130, b: 246, a: 1 };
  str = str.trim();
  const hexMatch = str.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hexMatch) {
    let h = hexMatch[1];
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16), a: 1 };
  }
  const rgba = str.match(/rgba?\(([^)]+)\)/i);
  if (rgba) {
    const parts = rgba[1].split(",").map((x) => parseFloat(x));
    return { r: parts[0], g: parts[1], b: parts[2], a: parts[3] ?? 1 };
  }
  return { r: 59, g: 130, b: 246, a: 1 };
}

function toOutput({ r, g, b }, a) {
  if (a >= 1) return rgbToHex({ r, g, b });
  return `rgba(${r}, ${g}, ${b}, ${a.toFixed(2)})`;
}

export function ColorPicker({ value, onChange }) {
  const init = parseColor(value);
  const initHsv = rgbToHsv(init.r, init.g, init.b);
  const [h, setH] = useState(initHsv.h);
  const [s, setS] = useState(initHsv.s);
  const [v, setV] = useState(initHsv.v);
  const [a, setA] = useState(init.a);
  const [hexInput, setHexInput] = useState(rgbToHex(init));
  const svRef = useRef(null);
  const draggingRef = useRef(null);

  const rgb = hsvToRgb(h, s, v);
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // sync external value changes (e.g., preset swatch)
  useEffect(() => {
    const p = parseColor(value);
    const hv = rgbToHsv(p.r, p.g, p.b);
    setH(hv.h); setS(hv.s); setV(hv.v); setA(p.a); setHexInput(rgbToHex(p));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = useCallback((nh, ns, nv, na) => {
    const c = hsvToRgb(nh, ns, nv);
    setHexInput(rgbToHex(c));
    onChange(toOutput(c, na));
  }, [onChange]);

  const handleSV = useCallback((e) => {
    const rect = svRef.current.getBoundingClientRect();
    const x = clamp((e.clientX - rect.left) / rect.width, 0, 1);
    const y = clamp((e.clientY - rect.top) / rect.height, 0, 1);
    const ns = x * 100, nv = (1 - y) * 100;
    setS(ns); setV(nv); emit(h, ns, nv, a);
  }, [h, a, emit]);

  useEffect(() => {
    const move = (e) => {
      const pt = e.touches ? e.touches[0] : e;
      if (draggingRef.current === "sv") handleSV(pt);
    };
    const up = () => { draggingRef.current = null; };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [handleSV]);

  const onHex = (val) => {
    setHexInput(val);
    const p = parseColor(val);
    if (val.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)) {
      const hv = rgbToHsv(p.r, p.g, p.b);
      setH(hv.h); setS(hv.s); setV(hv.v);
      onChange(toOutput(p, a));
    }
  };

  const setRgbChannel = (ch, val) => {
    const next = { ...rgb, [ch]: clamp(parseInt(val || 0), 0, 255) };
    const hv = rgbToHsv(next.r, next.g, next.b);
    setH(hv.h); setS(hv.s); setV(hv.v); setHexInput(rgbToHex(next));
    onChange(toOutput(next, a));
  };

  const inputCls = "w-full bg-black/20 rounded-lg px-2 py-1.5 text-xs font-mono text-center outline-none focus:ring-1";

  return (
    <div className="w-full space-y-3 no-select" data-testid="color-picker">
      <div
        ref={svRef}
        className="relative w-full h-40 rounded-xl cursor-crosshair overflow-hidden"
        style={{ background: `hsl(${h}, 100%, 50%)` }}
        onPointerDown={(e) => { draggingRef.current = "sv"; handleSV(e); }}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(to right, #fff, transparent)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #000, transparent)" }} />
        <div
          className="absolute w-4 h-4 rounded-full border-2 border-white shadow-lg pointer-events-none -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${s}%`, top: `${100 - v}%`, boxShadow: "0 0 0 1px rgba(0,0,0,0.3)" }}
        />
      </div>

      {/* Hue */}
      <div>
        <input
          type="range" min="0" max="360" value={h}
          onChange={(e) => { const nh = Number(e.target.value); setH(nh); emit(nh, s, v, a); }}
          className="w-full h-3 rounded-full appearance-none cursor-pointer"
          style={{ background: "linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)" }}
          data-testid="color-hue-slider"
        />
      </div>

      {/* Alpha */}
      <div className="rounded-full overflow-hidden" style={{ background: "linear-gradient(45deg,#ccc 25%,transparent 25%),linear-gradient(-45deg,#ccc 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#ccc 75%),linear-gradient(-45deg,transparent 75%,#ccc 75%)", backgroundSize: "10px 10px", backgroundPosition: "0 0,0 5px,5px -5px,-5px 0" }}>
        <input
          type="range" min="0" max="1" step="0.01" value={a}
          onChange={(e) => { const na = Number(e.target.value); setA(na); emit(h, s, v, na); }}
          className="w-full h-3 appearance-none cursor-pointer"
          style={{ background: `linear-gradient(to right, transparent, ${rgbToHex(rgb)})` }}
          data-testid="color-alpha-slider"
        />
      </div>

      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg border shrink-0" style={{ background: toOutput(rgb, a), borderColor: "var(--border-subtle)" }} />
        <input
          value={hexInput} onChange={(e) => onHex(e.target.value)}
          className={inputCls} style={{ color: "var(--text-primary)" }} data-testid="color-picker-input" placeholder="#HEX"
        />
      </div>

      <div className="grid grid-cols-3 gap-2">
        {["r", "g", "b"].map((ch) => (
          <div key={ch}>
            <label className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{ch}</label>
            <input type="number" value={rgb[ch]} onChange={(e) => setRgbChannel(ch, e.target.value)} className={inputCls} style={{ color: "var(--text-primary)" }} />
          </div>
        ))}
      </div>
      <div className="text-[10px] font-mono text-center" style={{ color: "var(--text-muted)" }}>
        HSL({hsl.h}, {hsl.s}%, {hsl.l}%) · A {a.toFixed(2)}
      </div>
    </div>
  );
}

export function ColorSwatch({ color, onChange, label, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button" onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 w-full px-2.5 py-2 rounded-xl border transition-all hover:scale-[1.01]"
        style={{ borderColor: "var(--border-subtle)", background: "var(--bg-card)" }}
        data-testid={testId}
      >
        <span className="w-6 h-6 rounded-md border shrink-0" style={{ background: color, borderColor: "var(--border-subtle)" }} />
        {label && <span className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>{label}</span>}
        <span className="ml-auto text-[10px] font-mono" style={{ color: "var(--text-muted)" }}>{color?.startsWith("#") ? color : "rgba"}</span>
      </button>
      {open && (
        <div className="absolute z-50 mt-2 w-64 p-3 rounded-2xl glass shadow-2xl left-0" style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
          <ColorPicker value={color} onChange={onChange} />
        </div>
      )}
    </div>
  );
}
